import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import BottomNav from '../../components/BottomNav'
import Spinner from '../../components/Spinner'
import { getOrders } from '../../api/orders'
import { useAuth } from '../../context/AuthContext'
import { formatMoney } from '../../utils/format'
import { Order } from '../../types'

const G = '#C89B4C'
const GB = 'rgba(200,155,76,0.14)'
const BD = 'rgba(244,238,225,0.14)'
const GLASS = {
  background: 'linear-gradient(155deg, rgba(255,248,235,0.05) 0%, rgba(255,248,235,0.015) 100%)',
  backdropFilter: 'blur(11px) saturate(150%)',
  WebkitBackdropFilter: 'blur(11px) saturate(150%)',
} as const
const TX = '#F4F1EA'
const TX2 = '#C8C4BB'
const TX3 = '#8A8480'
const SERIF = "'Newsreader', Georgia, serif"

const STATUS_MAP: Record<string, { label_ru: string; label_uz: string; color: string; bg: string }> = {
  pending:    { label_ru: 'На рассмотрении', label_uz: "Ko'rib chiqilmoqda", color: '#8A9DC0', bg: 'rgba(138,157,192,0.12)' },
  reviewing:  { label_ru: 'Проверка',        label_uz: "Ko'rib chiqish",     color: '#8A9DC0', bg: 'rgba(138,157,192,0.12)' },
  approved:   { label_ru: 'Одобрена',         label_uz: 'Tasdiqlangan',       color: G,          bg: GB },
  active:     { label_ru: 'Активна',          label_uz: 'Faol',               color: '#4CAF7E',  bg: 'rgba(76,175,126,0.12)' },
  closed:     { label_ru: 'Закрыта',          label_uz: 'Yopilgan',           color: TX3,        bg: 'rgba(138,132,128,0.10)' },
  rejected:   { label_ru: 'Отказ',            label_uz: 'Rad etilgan',        color: '#E05E5E',  bg: 'rgba(224,94,94,0.10)' },
  cancelled:  { label_ru: 'Отменена',         label_uz: 'Bekor qilindi',      color: TX3,        bg: 'rgba(138,132,128,0.10)' },
}

function OrderCard({ order, lang }: { order: Order; lang: string }) {
  const nav = useNavigate()
  const st = STATUS_MAP[order.status] ?? STATUS_MAP.pending
  const label = lang === 'uz' ? st.label_uz : st.label_ru
  const name = order.product
    ? (lang === 'uz' ? order.product.name_uz : order.product.name_ru)
    : (lang === 'uz' ? "Maxsus ariza" : 'Произвольная заявка')
  const paidCount = order.payments?.filter(p => p.status === 'paid').length ?? 0
  const totalCount = order.payments?.length ?? order.months

  return (
    <div
      onClick={() => nav(`/orders/${order.id}`)}
      style={{
        ...GLASS, border: `1px solid ${BD}`, borderRadius: 16,
        padding: '16px', marginBottom: 10, cursor: 'pointer',
        transition: 'border-color 0.15s',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: TX3, marginBottom: 4 }}>
            {order.registry_num}
          </p>
          <p style={{
            fontFamily: SERIF, fontStyle: 'italic', fontSize: 18, color: TX,
            lineHeight: 1.2, margin: 0,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {name}
          </p>
        </div>
        <span style={{
          marginLeft: 10, flexShrink: 0, fontSize: 10, fontWeight: 600,
          padding: '4px 9px', borderRadius: 99,
          color: st.color, background: st.bg,
          letterSpacing: '0.04em',
        }}>
          {label}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <p style={{ margin: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: 15, color: TX }}>
            {formatMoney(order.monthly_amount)}
            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: TX3, marginLeft: 4 }}>
              /{lang === 'uz' ? 'oy' : 'мес'}
            </span>
          </p>
          <p style={{ margin: '3px 0 0', fontSize: 11, color: TX3 }}>
            {lang === 'uz' ? `Jami: ${formatMoney(order.total_amount)}` : `Итого: ${formatMoney(order.total_amount)}`}
          </p>
        </div>

        {order.status === 'active' && totalCount > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: TX3 }}>
              {paidCount}/{totalCount}
            </span>
            <div style={{ width: 64, height: 3, background: 'rgba(244,238,225,0.10)', borderRadius: 99 }}>
              <div style={{
                width: `${(paidCount / totalCount) * 100}%`,
                height: '100%', background: '#4CAF7E', borderRadius: 99,
              }} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const TABS = [
  { value: '', label_ru: 'Все', label_uz: 'Barchasi' },
  { value: 'active', label_ru: 'Активные', label_uz: 'Faol' },
  { value: 'closed', label_ru: 'Закрытые', label_uz: 'Yopilgan' },
  { value: 'rejected', label_ru: 'Отказ/Отмена', label_uz: "Rad/Bekor" },
]

export default function OrdersPage() {
  const { user } = useAuth()
  const lang = user?.language ?? 'ru'
  const [tab, setTab] = useState('')

  const statusFilter = tab === 'rejected' ? undefined : tab

  const { data, isLoading } = useQuery({
    queryKey: ['my-orders', tab],
    queryFn: () => {
      if (tab === 'rejected') {
        return getOrders({ limit: 100 }).then(res => ({
          ...res,
          items: res.items.filter(o => o.status === 'rejected' || o.status === 'cancelled'),
        }))
      }
      return getOrders({ status: statusFilter || undefined, limit: 100 })
    },
    placeholderData: (prev) => prev,
  })

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 40, padding: '12px 20px',
        background: 'rgba(13,21,38,0.82)', backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)', borderBottom: `1px solid ${BD}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <h1 style={{ margin: 0, fontFamily: "'Inter', sans-serif", fontSize: 16, fontWeight: 600, color: TX }}>
          {lang === 'uz' ? 'Arizalarim' : 'Мои заявки'}
        </h1>
        {data && data.total > 0 && (
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: TX3 }}>
            {data.total}
          </span>
        )}
      </header>

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: 0, padding: '0 20px',
        borderBottom: `1px solid ${BD}`,
        background: 'rgba(13,21,38,0.50)',
        overflowX: 'auto',
      }}>
        {TABS.map(t => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            style={{
              padding: '12px 14px', position: 'relative', whiteSpace: 'nowrap',
              fontSize: 13, fontWeight: tab === t.value ? 600 : 400,
              color: tab === t.value ? TX : TX3,
              background: 'none', border: 'none', cursor: 'pointer',
            }}
          >
            {lang === 'uz' ? t.label_uz : t.label_ru}
            {tab === t.value && (
              <div style={{ position: 'absolute', left: 14, right: 14, bottom: -1, height: 1.5, background: G, borderRadius: 1 }} />
            )}
          </button>
        ))}
      </div>

      <main style={{ flex: 1, padding: '16px 20px 104px' }}>
        {isLoading ? (
          <Spinner className="mt-10" />
        ) : !data?.items.length ? (
          <EmptyState lang={lang} />
        ) : (
          <div>
            {data.items.map(o => <OrderCard key={o.id} order={o} lang={lang} />)}
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  )
}

function EmptyState({ lang }: { lang: string }) {
  const nav = useNavigate()
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 280, gap: 18, textAlign: 'center' }}>
      <div style={{ fontSize: 48 }}>📋</div>
      <div>
        <p style={{ color: TX2, fontSize: 14, margin: '0 0 6px' }}>
          {lang === 'uz' ? 'Arizalar yo\'q' : 'Нет заявок'}
        </p>
        <p style={{ color: TX3, fontSize: 12 }}>
          {lang === 'uz' ? "Nasiyaga ariza qoldiring" : 'Оформите рассрочку на любой товар'}
        </p>
      </div>
      <button
        onClick={() => nav('/catalog')}
        style={{
          padding: '12px 28px', borderRadius: 12,
          background: GB, border: `1.5px solid ${G}`,
          color: G, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}
      >
        {lang === 'uz' ? 'Katalogga o\'tish' : 'Перейти в каталог'}
      </button>
    </div>
  )
}
